import moment from 'moment-timezone';

export type GoogleBusyIntervalLike = {
  startUtc?: string;
  endUtc?: string;
  StartUtc?: string;
  EndUtc?: string;
};

/** Parse Google Busy API instant (always UTC) to epoch ms. */
export function parseGoogleBusyInstantUtcMs(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value !== null) {
    const nested =
      (value as { dateTime?: unknown; DateTime?: unknown; value?: unknown }).dateTime
      ?? (value as { dateTime?: unknown; DateTime?: unknown; value?: unknown }).DateTime
      ?? (value as { dateTime?: unknown; DateTime?: unknown; value?: unknown }).value;
    if (nested) return parseGoogleBusyInstantUtcMs(nested);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  let m = moment.utc(raw);
  if (!m.isValid() && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    m = moment.utc(`${raw.replace(/\.\d+$/, '')}Z`);
  }

  return m.isValid() ? m.valueOf() : null;
}

/** Standard UTC overlap — same rule as Google Calendar FreeBusy. */
export function doUtcInstantRangesOverlap(
  startAMs: number,
  endAMs: number,
  startBMs: number,
  endBMs: number
): boolean {
  if (!Number.isFinite(startAMs) || !Number.isFinite(endAMs) || !Number.isFinite(startBMs) || !Number.isFinite(endBMs)) {
    return false;
  }
  if (endAMs <= startAMs || endBMs <= startBMs) {
    return false;
  }
  return startAMs < endBMs && endAMs > startBMs;
}

export function isUtcRangeOverlappingGoogleBusyIntervals(
  startUtc: string,
  endUtc: string,
  googleBusy: GoogleBusyIntervalLike[] | null | undefined
): boolean {
  const bookingStartMs = parseGoogleBusyInstantUtcMs(startUtc);
  const bookingEndMs = parseGoogleBusyInstantUtcMs(endUtc);
  if (bookingStartMs == null || bookingEndMs == null) {
    return false;
  }

  if (!googleBusy || !Array.isArray(googleBusy) || googleBusy.length === 0) {
    return false;
  }

  for (let i = 0; i < googleBusy.length; i++) {
    const block = googleBusy[i];
    const busyStartMs = parseGoogleBusyInstantUtcMs(block.startUtc || block.StartUtc);
    const busyEndMs = parseGoogleBusyInstantUtcMs(block.endUtc || block.EndUtc);
    if (busyStartMs == null || busyEndMs == null) continue;
    if (doUtcInstantRangesOverlap(bookingStartMs, bookingEndMs, busyStartMs, busyEndMs)) {
      return true;
    }
  }

  return false;
}

/** Booking slot (Date instant) vs Google busy blocks — compare in UTC only. */
export function isSlotOverlappingWithGoogleBusyUtc(
  slotDate: Date,
  packageMinutes: number,
  googleBusy: GoogleBusyIntervalLike[] | null | undefined
): boolean {
  if (!slotDate || !(slotDate instanceof Date) || isNaN(slotDate.getTime())) {
    return false;
  }
  if (!googleBusy || !Array.isArray(googleBusy) || googleBusy.length === 0) {
    return false;
  }

  const slotStartMs = slotDate.getTime();
  const slotEndMs = slotStartMs + packageMinutes * 60 * 1000;

  for (let i = 0; i < googleBusy.length; i++) {
    const block = googleBusy[i];
    const busyStartMs = parseGoogleBusyInstantUtcMs(block.startUtc || block.StartUtc);
    const busyEndMs = parseGoogleBusyInstantUtcMs(block.endUtc || block.EndUtc);
    if (busyStartMs == null || busyEndMs == null) continue;
    if (doUtcInstantRangesOverlap(slotStartMs, slotEndMs, busyStartMs, busyEndMs)) {
      return true;
    }
  }

  return false;
}

/**
 * Busy API query param: ISO UTC, or wall-clock "yyyy-MM-dd HH:mm:ss" in `timezone`.
 * Without timezone, wall-clock is parsed in browser local (legacy).
 */
export function toGoogleBusyQueryUtcIso(value?: string, timezone?: string): string | undefined {
  if (!value) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;

  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed) || /^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const ms = parseGoogleBusyInstantUtcMs(trimmed);
    return ms == null ? undefined : new Date(ms).toISOString();
  }

  const wall = trimmed.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/);
  if (wall) {
    const tz = timezone && moment.tz.zone(timezone) ? timezone : moment.tz.guess();
    const parsed = moment.tz(`${wall[1]} ${wall[2]}`, 'YYYY-MM-DD HH:mm:ss', tz);
    return parsed.isValid() ? parsed.utc().toISOString() : undefined;
  }

  const ms = parseGoogleBusyInstantUtcMs(trimmed);
  return ms == null ? undefined : new Date(ms).toISOString();
}

/** Calendar view range strings → UTC ISO for Busy API (use consultant/user TZ). */
export function toGoogleBusyQueryRangeUtcIso(
  from?: string,
  to?: string,
  timezone?: string
): { fromIso?: string; toIso?: string } {
  return {
    fromIso: toGoogleBusyQueryUtcIso(from, timezone),
    toIso: toGoogleBusyQueryUtcIso(to, timezone),
  };
}
